import React, { useState, useEffect } from 'react';
import CookieConsent, { Cookies } from 'react-cookie-consent';
import CookieSettingsModal from './CookieSettingsModal';

const GA_TRACKING_ID = 'G-XXXXXXXXXX';

// --- GA Initialization Logic ---
// We now pass the consent state to the initialization function
const initializeGA = (consentState) => {
  // If Analytics consent is true, initialize GA
  if (consentState.analytics && typeof window !== 'undefined' && !window.GA_INITIALIZED) {
    const script1 = document.createElement('script');
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_TRACKING_ID}', { 'send_page_view': true });
    `;
    document.head.appendChild(script2);

    window.GA_INITIALIZED = true;
    console.log(`Google Analytics initialized with ID: ${GA_TRACKING_ID}`);
  } else {
      console.log('Analytics declined. GA not initialized.');
      // Add logic here to remove any existing GA cookies if needed
  }
  
  // You would add similar logic here for Marketing cookies (e.g., Facebook Pixel)
  // if (consentState.marketing) { /* initialize marketing scripts */ }
};

// --- Main Overlay Component ---
const CookieConsentWithOverlay = ({ children }) => {
  const [hasConsent, setHasConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Cookie Name stores the string 'true', but we need to store the detailed object
  const DETAILED_CONSENT_COOKIE_NAME = 'detailedCookieConsent'; 

  useEffect(() => {
    // Check if the detailed consent cookie exists
    const storedConsent = Cookies.get(DETAILED_CONSENT_COOKIE_NAME);
    if (storedConsent) {
      const consentObject = JSON.parse(storedConsent);
      setHasConsent(true);
      initializeGA(consentObject);
    }
    
    // Manage scroll blocking based on consent and modal visibility
    if (!hasConsent || showSettings) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [hasConsent, showSettings]);


  // --- Handlers for Consent Actions ---
  
  const saveConsent = (preferences) => {
    // 1. Set the detailed preferences in a cookie (must be a string)
    Cookies.set(DETAILED_CONSENT_COOKIE_NAME, JSON.stringify(preferences), { expires: 365 });
    
    // 2. Set the standard consent cookie (for react-cookie-consent)
    Cookies.set('myAwesomeCookieConsent', 'true', { expires: 365 }); 
    
    // 3. Initialize services based on saved preferences
    initializeGA(preferences);
    
    // 4. Update UI state
    setHasConsent(true);
    setShowSettings(false);
  };
  
  const handleAcceptAll = () => {
      saveConsent({ analytics: true, marketing: true, essential: true });
  };
  
  const handleDeclineAll = () => {
      saveConsent({ analytics: false, marketing: false, essential: true });
  };


  // --- Conditional Rendering ---

  // 1. If detailed settings are requested, show the modal
  if (showSettings) {
      return (
          <CookieSettingsModal 
              onSave={saveConsent}
              onAcceptAll={handleAcceptAll}
              onDeclineAll={handleDeclineAll}
          />
      );
  }
  
  // 2. If consent is NOT given (and no settings are being shown), show the initial banner
  if (!hasConsent) {
    return (
      <CookieConsent
        location="none"
        buttonText="Accept All"
        // CHANGE: Replaced Decline button with "Manage Settings"
        declineButtonText="Manage Settings" 
        enableDeclineButton
        cookieName="myAwesomeCookieConsent"
        expires={365}
        
        onAccept={handleAcceptAll}
        onDecline={() => setShowSettings(true)} // Open the detailed settings modal
        
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, 
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', 
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
        }}
        contentStyle={{
          maxWidth: '500px', padding: '40px', borderRadius: '8px', 
          backgroundColor: '#fff', color: '#333', textAlign: 'center',
        }}
        buttonStyle={{
          backgroundColor: '#007bff', color: 'white', fontSize: '16px', 
          fontWeight: 'bold', borderRadius: '5px', padding: '10px 20px', margin: '10px',
        }}
        // Style for the "Manage Settings" button
        declineButtonStyle={{
          backgroundColor: '#6c757d', color: 'white',
          fontSize: '16px', borderRadius: '5px', padding: '10px 20px', margin: '10px',
        }}
      >
        <h2 style={{marginTop: 0}}>Data Privacy Notice</h2>
        <p>We use cookies to enhance user experience and analyze traffic. Click "Accept All" for the best experience, or "Manage Settings" to customize your choice.</p>
      </CookieConsent>
    );
  }
  
  // 3. If consent is given, render the application content
  return <>{children}</>;
};

export default CookieConsentWithOverlay;